import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import type { UserMembership } from '@/src/api/authApi';
import membershipsApi from '@/src/api/membershipsApi';

/**
 * useFetchMyMemberships — `GET /me/memberships`, behind `app/my-memberships`.
 *
 * The list IS the page here, unlike [[useFetchReservations]] where the profile's
 * details render either way — so loading, error and empty each replace the whole
 * body. Same three-state union and the same `retry` / `refresh` pair, for the
 * reasons that hook gives: `retry` clears back to `loading` so the failure can
 * redraw its bones, `refresh` leaves what's on screen alone and resolves when it
 * settles, which is what pull-to-refresh needs.
 *
 * No mapping layer, again unlike that hook: a row here IS a `UserMembership`, and
 * the screen groups them by the `phase` the server already derived.
 */

export type FetchMyMembershipsState = SettledState & {
  retry: () => void;
  refresh: () => Promise<void>;
};

/** `memberships` is present iff the fetch succeeded — `[]` is a real answer. */
type SettledState =
  | { status: 'loading'; memberships: null; error: null }
  | { status: 'error'; memberships: null; error: PSErrorClass }
  | { status: 'success'; memberships: UserMembership[]; error: null };

const LOADING: SettledState = {
  status: 'loading',
  memberships: null,
  error: null,
};

export function useFetchMyMemberships(): FetchMyMembershipsState {
  const [state, setState] = useState<SettledState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const memberships = await membershipsApi.listMyMemberships();
      if (isMounted.current)
        setState({ status: 'success', memberships, error: null });
    } catch (error: unknown) {
      if (isMounted.current)
        setState({
          status: 'error',
          memberships: null,
          error: asApiError(error),
        });
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load` only setStates after awaiting the request, so the
    // write lands in a later microtask, not synchronously in this body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  // A counter rather than a bare re-run: a retry that fails the SAME way must
  // still arrive as a fresh `PSErrorClass`.
  const retry = useCallback(() => {
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry, refresh: load };
}
